import { Input, Button, Checkbox, Chip } from 'common/components';
import { PlusIcon, MinusIcon } from 'lucide-react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { WorkflowConfigFormData } from 'pages/WorkflowBuilder/AgenticWorkflowBuilder';

// Written by click-to-call and inbound unknown-caller rather than by any
// transition, so it needs a caption to explain itself among the business ones.
const SLAI_ORIGIN_STATUS = 'New SLAI Lead';

/**
 * Defines the workflow's party status vocabulary.
 *
 * Terminal and Send to CRM are not decoration. The backend requires at least one
 * terminal and one non-terminal status, so a form without Terminal could never
 * produce a valid custom set, and Send to CRM gates the actual CRM update, so
 * without it a CRM name is stored and never used.
 *
 * A new workflow arrives prefilled with the set creation would seed anyway.
 */
export const DefinePartyStatusTab = () => {
    const {
        control,
        register,
        getValues,
        watch,
        formState: { errors },
    } = useFormContext<WorkflowConfigFormData>();

    const { fields, append, remove } = useFieldArray({ control, name: 'partyStatuses' });
    const statuses = watch('partyStatuses') ?? [];
    const transitions = watch('statusTransitions') ?? [];

    const hasTerminal = statuses.some((s) => s.terminal);
    const hasNonTerminal = statuses.some((s) => !s.terminal);

    return (
        <div className='flex flex-col gap-lg pt-xl'>
            <p className='text-gray-700'>
                Define the statuses a party can hold in this workflow. Mark a status terminal to stop contacting the
                party once it is reached. These can only be set while creating the workflow.
            </p>

            {statuses.length > 0 && (
                <p className='text-gray-700'>
                    This is the set the workflow will use. Edit or remove entries to replace it with your own.
                </p>
            )}

            <div className='flex justify-end'>
                <Button
                    variant='secondary'
                    icon={PlusIcon}
                    onClick={() => append({ name: '', crmIntegrationName: '', terminal: false, pushToCrm: false })}
                >
                    Add Status
                </Button>
            </div>

            {fields.length === 0 && (
                // Saving without touching this is a supported path, and the wording
                // has to hold for both kinds of tenant: a generic one is seeded with
                // the default vocabulary, while one with dedicated behaviour gets no
                // config and keeps its own. The frontend cannot tell them apart, so
                // it says "your tenant's" rather than naming either.
                <p className='text-gray-700'>
                    No custom statuses defined. This workflow will use your tenant&apos;s default status model — add
                    statuses here to replace it.
                </p>
            )}

            <div className='flex flex-col gap-lg'>
                {fields.map((field, index) => (
                    <div key={field.id} className='flex flex-col gap-sm' id={`partyStatuses-${index}`}>
                        <div className='flex gap-sm'>
                            <Input
                                label='Name'
                                placeholder='e.g. New Lead'
                                error={!!errors.partyStatuses?.[index]?.name}
                                errorText={errors.partyStatuses?.[index]?.name?.message}
                                {...register(`partyStatuses.${index}.name`, {
                                    required: 'Name is required',
                                    validate: (value) => {
                                        const trimmed = value?.trim();
                                        if (!trimmed) return true;
                                        const all = getValues('partyStatuses') || [];
                                        const duplicate = all.some(
                                            (s, i) =>
                                                i !== index && s?.name?.trim().toLowerCase() === trimmed.toLowerCase()
                                        );
                                        return !duplicate || 'A status with this name already exists';
                                    },
                                })}
                            />
                            <Input
                                label='CRM Name'
                                placeholder='e.g. new_lead'
                                error={!!errors.partyStatuses?.[index]?.crmIntegrationName}
                                errorText={errors.partyStatuses?.[index]?.crmIntegrationName?.message}
                                {...register(`partyStatuses.${index}.crmIntegrationName`, {
                                    validate: (value) => {
                                        // Mirrors the server rule so it surfaces here rather than
                                        // failing the whole workflow save.
                                        const pushToCrm = getValues(`partyStatuses.${index}.pushToCrm`);
                                        if (pushToCrm && !value?.trim()) {
                                            return 'Required to send this status to the CRM';
                                        }
                                        return true;
                                    },
                                })}
                            />
                            <Button
                                variant='secondary'
                                icon={MinusIcon}
                                aria-label={`Remove status ${index + 1}`}
                                onClick={() => {
                                    // A transition targeting a removed status fails validation on
                                    // save, from a screen the user has since left.
                                    const name = getValues(`partyStatuses.${index}.name`)?.trim().toLowerCase();
                                    const used = transitions.some((t) => t.target?.trim().toLowerCase() === name);
                                    if (used) return;
                                    remove(index);
                                }}
                                disabled={transitions.some(
                                    (t) =>
                                        t.target?.trim().toLowerCase() ===
                                        getValues(`partyStatuses.${index}.name`)?.trim().toLowerCase()
                                )}
                            />
                        </div>

                        <div className='flex gap-lg'>
                            <Controller
                                name={`partyStatuses.${index}.terminal`}
                                control={control}
                                render={({ field: terminalField }) => (
                                    <Checkbox
                                        label='Terminal status'
                                        checked={!!terminalField.value}
                                        onChange={(e) => terminalField.onChange(e.target.checked)}
                                    />
                                )}
                            />
                            <Controller
                                name={`partyStatuses.${index}.pushToCrm`}
                                control={control}
                                render={({ field: pushField }) => (
                                    <Checkbox
                                        label='Send to CRM'
                                        checked={!!pushField.value}
                                        onChange={(e) => pushField.onChange(e.target.checked)}
                                    />
                                )}
                            />
                            {statuses[index]?.terminal && (
                                <Chip variant='filled' color='warning' size='sm'>
                                    We stop contacting parties on this status
                                </Chip>
                            )}
                            {statuses[index]?.name?.trim() === SLAI_ORIGIN_STATUS && (
                                <Chip variant='filled' color='info' size='sm'>
                                    Where parties start when SLAI originates the contact
                                </Chip>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {/*
              The server rejects an all-terminal or all-non-terminal set. Warn while
              the user can still act on it rather than failing the workflow save.
            */}
            {statuses.length > 0 && !hasTerminal && (
                <Chip variant='filled' color='error' size='sm'>
                    Add at least one terminal status, or parties can never finish this workflow
                </Chip>
            )}
            {statuses.length > 0 && !hasNonTerminal && (
                <Chip variant='filled' color='error' size='sm'>
                    Add at least one non-terminal status, or no party can be worked
                </Chip>
            )}
        </div>
    );
};
